// FSSAILicenseRenewal.jsx
import React from "react";

export default function FSSAILicenseRenewal() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            FSSAI License Renewal
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            FSSAI License Renewal for Food Businesses
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks provides timely assistance for FSSAI License
            Renewal so that food businesses maintain uninterrupted legal
            validity under the Food Safety and Standards framework.
          </p>
          <p className="text-gray-700">
            The renewal process is managed with a focus on deadline tracking,
            updated disclosures, and compliance checks to minimise the risk of
            expiry, penalties, or operational disruption.
          </p>
        </div>

        {/* Right: Image (FSSAI LICENSE RENEWAL - food business compliance renewal process) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/5665667/pexels-photo-5665667.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Food business renewing FSSAI license"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks offers structured assistance for FSSAI
              License Renewal, helping businesses stay compliant without last
              minute rush or filing errors.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Tracking renewal due dates and eligibility for renewal or
                  modification of existing licenses.
                </li>
                <li>
                  Verifying changes in business operations, turnover, or
                  locations that must be reflected at the time of renewal.
                </li>
                <li>
                  Preparing and submitting renewal applications in line with
                  current FSSAI norms and portal requirements.
                </li>
                <li>
                  Assisting with queries, inspections, and clarifications raised
                  by licensing authorities during renewal.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for FSSAI License Renewal
            </h2>
            <p className="text-gray-700 mb-4">
              Renewal typically uses the existing license as a base, updated
              with current business details and compliance declarations.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Existing FSSAI license copy with license number and validity
                  details.
                </li>
                <li>
                  Updated business details such as address, contact information,
                  nature of food business, and any new locations.
                </li>
                <li>
                  Compliance declarations confirming adherence to food safety
                  norms, hygiene standards, and regulatory conditions.
                </li>
                <li>
                  Any additional records or documents requested by the authority
                  based on the category and scale of food business.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The renewal process is handled with a proactive, compliance-first
              mindset to avoid any lapse in FSSAI licensing.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Proactive Renewal Management
                </h3>
                <p className="text-gray-700 text-sm">
                  Systematic tracking of expiry timelines and reminders so your
                  license renewal is initiated well before the due date.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Error-Free Compliance Handling
                </h3>
                <p className="text-gray-700 text-sm">
                  Focus on accurate forms, updated information, and correct
                  declarations to reduce the chances of rejection or delay.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Reliable Professional Service
                </h3>
                <p className="text-gray-700 text-sm">
                  Dedicated support throughout the renewal lifecycle, from
                  documentation to follow-up with FSSAI authorities.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Renew Your FSSAI License on Time
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to plan your FSSAI license
                renewal in advance and keep your food business compliant without
                interruption.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
